import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
  Fade,
  Slide,
  CircularProgress,
  alpha,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Phone,
  Email,
  Security,
  VerifiedUser,
  CarRental,
  Inventory,
  TrendingUp,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import LogoImage from '../../assets/logo.png';
import { useSearchParams } from 'react-router-dom';

interface LoginFormData {
  username: string; // mobile or email
  password: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('sessionExpired') === 'true';


  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      username: '',
      password: '',
    },
    mode: 'onChange',
  });

  // Handle success message from registration
  useEffect(() => {
    const state = location.state as { message?: string; type?: string; from?: string } | null;
    if (state?.message && state?.type === 'success') {
      setSuccessMessage(state.message);
    }
  }, [location.state]);

  // Handle redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      setSuccessMessage('');
      setIsSubmitting(true);
      
      // Validate if it's email or mobile
      const mobileRegex = /^\d+$/;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      // Check if input is valid
      if (!mobileRegex.test(data.username) && !emailRegex.test(data.username)) {
        setError('Please enter a valid email address or mobile number');
        setIsSubmitting(false);
        return;
      }
      
      // Call login with ONE object containing both fields
      const success = await login({
        username: data.username,
        password: data.password
      });
      
      if (success) {
        setSuccessMessage('Login successful! Redirecting to dashboard...');
        
        // Redirect to dashboard or previous page
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 1000);
      } else {
        setError('Invalid credentials. Please try again.');
      }
      
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation function for username (mobile or email)
  const validateUsername = (value: string) => {
    if (!value) return 'Email or mobile number is required';
    
    const mobileRegex = /^\d+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (mobileRegex.test(value) || emailRegex.test(value)) {
      return true;
    }
    
    return 'Please enter a valid email address or mobile number';
  };  

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(33, 150, 243, 0.1) 0%, transparent 50%)',
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Left side - Branding and Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Fade in timeout={800}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box
                  onClick={() => navigate('/')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    mb: 3,
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <Box sx={{ width: 80, height: 80, borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)' }}>
                    <img src={LogoImage} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ ml: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Sadaka
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.dark,
                      }}
                    >
                      Enterprise 1
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    color: 'primary.dark',
                  }}
                >
                  Vehicle Inventory Management
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  paragraph
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 4,
                    maxWidth: 500,
                  }}
                >
                  Access the complete vehicle management system to track purchases, 
                  sales, inventory, and profit/loss analytics for your automotive business.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Inventory sx={{ color: 'primary.main', mr: 2 }} />
                    <Typography variant="body1">
                      Real-time inventory tracking
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ color: 'success.main', mr: 2 }} />
                    <Typography variant="body1">
                      Profit & loss analytics
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Security sx={{ color: 'secondary.main', mr: 2 }} />
                    <Typography variant="body1">
                      Secure data protection
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Right side - Login Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Fade in timeout={1200}>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 'none',
                    background: 'transparent',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                    <Slide direction="down" in timeout={600}>
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                          variant="h4"
                          component="h1"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            color: 'primary.dark',
                          }}
                        >
                          Staff Login
                        </Typography>                       
                        <Divider sx={{ my: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            For authorized personnel only
                          </Typography>
                        </Divider>
                      </Box>
                    </Slide>
                    
                    {sessionExpired && !error && !successMessage && (
                      <Slide direction="left" in timeout={400}>
                        <Alert
                          severity="warning"
                          sx={{
                            mb: 3,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                          }}
                        >
                          Your session has expired. Please sign in again.
                        </Alert>
                      </Slide>
                    )}
                    
                    {/* Alerts */}
                    {error && (
                      <Slide direction="left" in timeout={400}>
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mb: 3, 
                            borderRadius: 2,
                            animation: 'shake 0.5s',
                            '@keyframes shake': {
                              '0%, 100%': { transform: 'translateX(0)' },
                              '25%': { transform: 'translateX(-5px)' },
                              '75%': { transform: 'translateX(5px)' },
                            },
                          }}
                        >
                          {error}
                        </Alert>
                      </Slide>
                    )}

                    {successMessage && (
                      <Slide direction="left" in timeout={400}>
                        <Alert 
                          severity="success" 
                          sx={{ 
                            mb: 3, 
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                          }}
                        >
                          {successMessage}
                        </Alert>
                      </Slide>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Controller
                        name="username"
                        control={control}
                        rules={{
                            validate: validateUsername,
                        }}
                        render={({ field }) => (
                            <TextField
                            {...field}
                            fullWidth
                            label="Email or Mobile Number"
                            placeholder="user@company.com or 01XXXXXXXXX"
                            error={!!errors.username}
                            helperText={errors.username?.message}
                            InputProps={{
                                startAdornment: (
                                <InputAdornment position="start">
                                    <Email color="action" />
                                </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover': {
                                    '& fieldset': {
                                    borderColor: theme.palette.primary.main,
                                    },
                                },
                                },
                            }}
                            />
                        )}
                        />

                        <Controller
                          name="password"
                          control={control}
                          rules={{
                            required: 'Password is required',
                            minLength: {
                              value: 6,
                              message: 'Password must be at least 6 characters',
                            },
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Password"
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Enter your password"
                              error={!!errors.password}
                              helperText={errors.password?.message}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Lock color="action" />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => setShowPassword(!showPassword)}
                                      edge="end"
                                      aria-label="toggle password visibility"
                                      sx={{
                                        color: theme.palette.grey[600],
                                        '&:hover': {
                                          color: theme.palette.primary.main,
                                        },
                                      }}
                                    >
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '&:hover': {
                                    '& fieldset': {
                                      borderColor: theme.palette.primary.main,
                                    },
                                  },
                                },
                              }}
                            />
                          )}
                        />

                        <Box sx={{ textAlign: 'right', mb: 1 }}>
                          <Link 
                            to="/forgot-password"
                            style={{ 
                              textDecoration: 'none',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                                transition: 'color 0.2s ease',
                              }}
                            >
                              Forgot your password?
                            </Typography>
                          </Link>
                        </Box>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={isSubmitting || isLoading}
                          sx={{
                            mt: 2,
                            py: 1.8,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                              boxShadow: '0 6px 24px rgba(25, 118, 210, 0.4)',
                              transform: 'translateY(-2px)',
                            },
                            '&:disabled': {
                              background: theme.palette.grey[300],
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {isSubmitting || isLoading ? (
                            <>
                              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                              Signing In...
                            </>
                          ) : (
                            'Sign In'
                          )}
                        </Button>
                      </Box>
                    </form>
                    <Divider sx={{ my: 2 }}>
                    </Divider>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Need assistance with your account?
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        Contact System Administrator
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;